import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Target, Shield, Truck, Headphones, Award, Users, Heart, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { getPageContent } from '../services/firebaseService';

const iconMap = {
  shield: Shield,
  truck: Truck,
  headphones: Headphones,
  award: Award,
  users: Users,
  heart: Heart,
};

const AboutPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const data = await getPageContent('about');
      setContent(data?.content || {});
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = content?.features || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1e3a5f] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-24">
      {/* Header */}
      <div className="bg-[#1e3a5f] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-white">Ana Səhifə</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-white">Haqqımızda</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">
            {content?.title || 'Haqqımızda'}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
              Biz Kimik?
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              {content?.description || 'PolyMax Medicine, Azərbaycanda əczaçılıq sahəsində lider şirkətlərdən biridir. Biz 2010-cu ildən bəri fəaliyyət göstəririk və müştərilərimizə yüksək keyfiyyətli tibbi məhsullar təqdim edirik.'}
            </p>
            <div className="flex items-center gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-[#1e3a5f]">15+</p>
                <p className="text-sm text-slate-500">İllik Təcrübə</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#1e3a5f]">500+</p>
                <p className="text-sm text-slate-500">Məhsul</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#1e3a5f]">10K+</p>
                <p className="text-sm text-slate-500">Müştəri</p>
              </div>
            </div>
          </div>
          <div className="relative animate-fade-in-up">
            {content?.image_url && (
              <img
                src={content.image_url}
                alt="PolyMax Medicine"
                className="rounded-2xl shadow-2xl w-full"
              />
            )}
          </div>
        </div>

        {/* Mission Only */}
        <div className="mb-20">
          <Card className="card-hover border-l-4 border-l-[#1e3a5f] max-w-3xl mx-auto">
            <CardContent className="p-8">
              <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-[#1e3a5f]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">Missiyamız</h3>
              <p className="text-slate-600 leading-relaxed">
                {content?.mission || 'Missiyamız insanların sağlamlığını qorumaq və onlara keyfiyyətli dərmanlar təqdim etməkdir.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">Dəyərlərimiz</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          <Card className="card-hover text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-[#1e3a5f]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">Keyfiyyət</h3>
              <p className="text-slate-600">
                Hər məhsulumuz beynəlxalq standartlara uyğun hazırlanır
              </p>
            </CardContent>
          </Card>
          <Card className="card-hover text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-[#3b82f6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-[#3b82f6]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">Təhlükəsizlik</h3>
              <p className="text-slate-600">
                Məhsullarımızın təhlükəsizliyi prioritetimizdir
              </p>
            </CardContent>
          </Card>
          <Card className="card-hover text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-[#25d366]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-[#25d366]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">Güvənlik</h3>
              <p className="text-slate-600">
                Müştərilərimizin güvəni biznеs əsasımızdır
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">Niyə Bizi Seçməlisiniz?</h2>
          </div>
          
          {/* Features Grid */}
          {features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {features.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || Shield;
                return (
                  <Card key={index} className="card-hover">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a5f] to-[#3b82f6] rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Benefits */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-8">
              Üstünlüklərimiz
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Beynəlxalq standartlara uyğun məhsullar',
                '15+ illik təcrübə və ekspertiza',
                'Peşəkar əczaçılar və mütəxəssislər',
                '24/7 müştəri dəstəyi',
                'Sürətli və etibarlı çatdırılma',
                'Rəqabətədavamlı qiymətlər'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#25d366]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#25d366]" />
                  </div>
                  <span className="text-slate-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-[#1e3a5f] rounded-2xl p-12 mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-5xl font-bold mb-2">15+</p>
              <p className="text-white/70">İllik Təcrübə</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">500+</p>
              <p className="text-white/70">Məhsul</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">10K+</p>
              <p className="text-white/70">Müştəri</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">98%</p>
              <p className="text-white/70">Məmnuniyyət</p>
            </div>
          </div>
        </div>

        {/* Collaboration Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">Əməkdaşlıq</h2>
          <div className="prose max-w-none">
            <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-line">
              {content?.collaboration || 'Biz şirkətlər, distribyutorlar və sağlamlıq təşkilatları ilə əməkdaşlıq etməyə açığıq. Əməkdaşlıq təklifləriniz üçün bizimlə əlaqə saxlayın.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
